import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, Loader2 } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_labresult-hub-1/artifacts/td8pjg41_FACTUAL%20LOGO.png";
const BG_IMAGE = "https://customer-assets.emergentagent.com/job_9e3eb863-9f56-4b53-ae60-1c92c3c49a59/artifacts/r9oqp3vw_2ad7d7f7-6692-4852-8818-dd6bea3162a1.JPG";

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    const result = await login(email, password);
    setLoading(false);

    if (result.success) {
      toast.success('Welcome back!');
      navigate('/dashboard');
    } else {
      toast.error(result.error);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex" data-testid="login-page">
      {/* Left Side - Form */}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8">
        <div className="w-full max-w-md">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 mb-8 group" data-testid="login-logo-link">
            <img 
              src={LOGO_URL} 
              alt="Factual Medical Diagnostic Centre" 
              className="w-14 h-14 rounded-full object-cover"
            />
            <div>
              <span className="text-xl font-bold text-slate-900 dark:text-white tracking-tight block">Factual Medical</span>
              <span className="text-sm text-slate-600 dark:text-slate-400">Diagnostic Centre</span>
            </div>
          </Link>

          <div className="card-clinical">
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">Welcome back</h1>
            <p className="text-slate-600 dark:text-slate-400 mb-6">Sign in to access your medical dashboard</p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="email" className="text-slate-700 dark:text-slate-300">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="input-clinical mt-1"
                  data-testid="login-email-input"
                />
              </div>

              <div>
                <Label htmlFor="password" className="text-slate-700 dark:text-slate-300">Password</Label>
                <div className="relative mt-1">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="input-clinical pr-10"
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between text-sm">
                <label className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                  <input type="checkbox" className="rounded border-slate-300 dark:border-slate-600" />
                  Remember me
                </label>
                <a href="#" className="text-sky-600 hover:text-sky-700 dark:text-sky-400">Forgot password?</a>
              </div>

              <Button 
                type="submit" 
                className="w-full bg-sky-600 hover:bg-sky-700"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  'Sign In'
                )}
              </Button>
            </form>

            <p className="mt-6 text-center text-sm text-slate-600 dark:text-slate-400">
              Don't have an account?{' '}
              <Link to="/register" className="text-sky-600 hover:text-sky-700 dark:text-sky-400 font-medium" data-testid="login-register-link">
                Create one
              </Link>
            </p>
          </div>
        </div>
      </div>

      {/* Right Side - Image */}
      <div 
        className="hidden lg:flex lg:flex-1 items-center justify-center p-12 relative"
        style={{
          backgroundImage: `url(${BG_IMAGE})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-sky-900/60"></div>
        <div className="relative max-w-lg text-white z-10">
          <h2 className="text-3xl font-bold mb-4">Your Health Journey Starts Here</h2>
          <p className="text-sky-100 text-lg">
            Access your lab results, book new tests, and manage your medical records 
            all in one secure platform.
          </p>
          <div className="mt-8 grid grid-cols-2 gap-4">
            <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4">
              <p className="text-2xl font-bold">50K+</p>
              <p className="text-sky-100 text-sm">Tests Completed</p>
            </div>
            <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4">
              <p className="text-2xl font-bold">4.9★</p>
              <p className="text-sky-100 text-sm">Patient Rating</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
